import { useEffect, useRef, useState } from 'react';

export function prefereMenosMovimento(): boolean {
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Conta do valor anterior até o novo (count-up) em `duracaoMs`. Com movimento reduzido, ou sem
 * requestAnimationFrame (testes), salta direto para o valor final.
 */
export function useContagem(alvo: number, duracaoMs = 600): number {
  const [valor, setValor] = useState(alvo);
  const anterior = useRef(alvo);

  useEffect(() => {
    const inicio = anterior.current;
    anterior.current = alvo;
    if (
      inicio === alvo ||
      prefereMenosMovimento() ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      setValor(alvo);
      return;
    }
    // O relógio do primeiro quadro é a referência: o timestamp do rAF pode ser anterior a um
    // performance.now() tirado aqui, o que daria progresso negativo.
    let t0: number | null = null;
    let quadro = 0;
    const passo = (agora: number) => {
      t0 ??= agora;
      const progresso = Math.min(1, Math.max(0, (agora - t0) / duracaoMs));
      const suave = 1 - (1 - progresso) ** 3;
      setValor(Math.round(inicio + (alvo - inicio) * suave));
      if (progresso < 1) quadro = requestAnimationFrame(passo);
    };
    quadro = requestAnimationFrame(passo);
    return () => {
      cancelAnimationFrame(quadro);
    };
  }, [alvo, duracaoMs]);

  return valor;
}

/**
 * Envolve uma mudança de estado numa View Transition quando o navegador suporta. A atualização
 * pode ser assíncrona: o navegador só fotografa o estado novo quando a promessa resolve (útil
 * quando a tela muda um tick depois, como nas notificações do TanStack Query).
 */
export function comTransicao(atualizar: () => void | Promise<void>): void {
  if ('startViewTransition' in document && !prefereMenosMovimento()) {
    document.startViewTransition(atualizar);
    return;
  }
  void atualizar();
}
